  <template>
    <div :class="['early-settlement-right-content', { 'empty-state': (isEmpty || isRequestCreated) && !calculationLoading, 'has-error': hasValidationError }]">
    <div v-if="calculationLoading" class="early-settlement-right-content-calculation-result">
      <div class="loading">
        <img src="@/assets/images/loading.gif" alt="Loading" />
      </div>
    </div>
    <div v-else-if="isRequestCreated" class="early-settlement-right-content-empty">
      <div class="warning-area">
        <img src="@/assets/images/info-warning.svg" />
        <h2>{{ warningTitle }}</h2>
        <span class="case">
          <p v-html="warningMessage"></p>
        </span>
      </div>
    </div>
    <div v-else-if="isEmpty" class="early-settlement-right-content-empty">
      <div v-if="isMaxRequestableAmountZero && !availabilityLoading" class="warning-area">
        <img src="@/assets/images/info-warning.svg" />
        <h2>Şu anda erken ödeme talebi oluşturamazsınız</h2>
        <span class="case">
          <p>Talep edebileceğiniz en yüksek tutar <span class="bold"> {{ formattedMinRequestAmount }} TL'ye ulaştığında </span> <br> erken ödeme talep edebilirsiniz.</p>
          <p>Talep oluşturabilmek için <br> bakiyenizin artmasını bekleyebilirsiniz.</p>
        </span>
      </div>
      <div v-else-if="isBelowMinRequestAmount && !availabilityLoading" class="warning-area">
        <img src="@/assets/images/info-warning.svg" />
        <h2>Şu anda erken ödeme talebi oluşturamazsınız</h2>
        <span class="case">
          <p>Talep edebileceğiniz en yüksek tutar <span class="bold"> {{ formattedMinRequestAmount }} TL’ye ulaştığında</span><br>erken ödeme talep edebilirsiniz.</p>
          <p>Talep oluşturabilmek için<br>bakiyenizin artmasını bekleyebilirsiniz.</p>
        </span>
      </div>
      <div v-else-if="isCurrentBalanceZero && !availabilityLoading" class="warning-area">
        <img src="@/assets/images/info-warning.svg" />
        <h2>{{ warningTitle }}</h2>
        <span class="case">
          <p>Erken ödeme talebi koşullarını karşılayacak <br>yeterli bakiyeniz bulunmamaktadır.</p>
          <p>Talep oluşturabilmek için<br> bakiyenizin artmasını bekleyebilirsiniz.</p>
        </span>
      </div>
      <div v-else-if="!isCreationAllowed && !availabilityLoading" class="warning-area">
        <img src="@/assets/images/info-warning.svg" />
        <h2>{{ warningTitle }}</h2>
        <span class="case">
          <p v-html="warningMessage"></p>
        </span>
      </div>
      <div v-else class="empty-area">
        <img src="@/assets/images/coupon.svg" />
        <h2>Erken Ödeme Tutarını Belirleyin</h2>
        <p>Lütfen sol tarafta bulunan alana <br> <b>"Erken Ödeme Talep Ettiğiniz"</b> tutarı girin.  </p>
        <p>Girdiğiniz tutar, <b>"Talep Edebileceğiniz En Yüksek <br> Tutar"</b>dan fazla olmamalıdır. </p>
      </div>
    </div>  
    <div v-else class="early-settlement-right-content-calculation-result">
        <h2>Hesaplama Sonucu</h2>
        <div class="balance-area">
          <div class="result-item">
            <div class="result-item-label">
              <span>Siparişlerinize Göre Hesaplanan Talep Tutarı</span>
              <TooltipPlugin tooltipText="Mevcut siparişlerinizden doğan hak edişlerinize göre sipariş tutarları baz alınarak sizin talep ettiğiniz tutara en yakın uygun tutar hesaplandı.">
                <template #tooltip-btn>
                  <Icons 
                    name="InfoCircle" 
                    color="#7382a0" 
                    size="sm"
                    class="info-icon"
                  />
                </template>
              </TooltipPlugin>
            </div>
          <span>₺{{ calculation?.calculatedRequestAmount }}</span>
          </div>
          <div class="result-item">
            <div class="result-item-label">
              <span>Erken Ödenen Gün Sayısı</span>
              <TooltipPlugin tooltipText="Erken ödemeye dahil edilen siparişlerin normal ödeme günleri ile erken ödeme tarihi arasındaki gün farklarının ortalaması olarak hesaplanır.">
                <template #tooltip-btn>
                  <Icons 
                    name="InfoCircle" 
                    color="#7382a0" 
                    size="sm"
                    class="info-icon"
                  />
                </template>
              </TooltipPlugin>
            </div>
            <span>{{ calculation?.earlyPaidDayCount }} Gün</span>
          </div>
          <div class="result-item">
            <span>n11 Günlük Hizmet Komisyon Oranı</span> 
            <span>%{{ calculation?.dailyServiceCommissionRate }}</span>
          </div>
          <div class="result-item">
            <div class="result-item-label">
              <span>Uygulanacak Hizmet Oranı</span> 
              <span class="result-item-label-right">({{ calculation?.earlyPaidDayCount }} x %{{ calculation?.dailyServiceCommissionRate }})</span>
            </div>
            <span>%{{ calculation?.appliedServiceRate }}</span>
          </div>
          <div class="result-item">
            <div class="result-item-label">
              <span>Uygulanacak Hizmet Bedeli</span> 
              <span class="result-item-label-right">(₺{{ calculation?.calculatedRequestAmount }} x %{{ calculation?.appliedServiceRate }})</span>
            </div> 
            <span><template v-if="Number(calculation?.serviceFee) !== 0">- </template>₺{{ calculation?.serviceFee }}</span>
          </div>
          <div class="result-item">
            <div class="result-item-label">
              <span>KDV (%20)</span> 
              <span class="result-item-label-right">(₺{{ calculation?.serviceFee }} x %20)</span>
            </div> 
            <span><template v-if="Number(calculation?.vatAmount) !== 0">- </template>₺{{ calculation?.vatAmount }}</span>
          </div>
          <div class="result-item total-amount">
            <span>Hesabınıza Yatacak Net Tutar</span> 
            <span>₺{{ calculation?.netPayableAmount }}</span>
          </div>
          </div>

          <div class="agreement">
          <input 
            class="agreement-checkbox" 
            type="checkbox" 
            v-model="isChecked"
            @change="handleCheckboxChange"
          />
          <span> <a href="#" @click.prevent="openContractModal" class="agreement-link">Erken Ödeme Sözleşmesi</a>'ni okudum ve kabul ediyorum.</span>
        </div>

        <AppButton
        primary
        lg
        title="Erken Ödeme Talep Et"
        :disabled="!isChecked"
        @click="openApprovalModal"
        :width="580"
        />
    </div>
  </div>

  <!-- Modalları Component Olarak Kullan -->
  <ContractModal ref="contractModalRef" @accept="acceptContract" />
  <ApprovalModal
    ref="approvalModalRef"
    :calculation="calculation"
    :commonGA4Params="getCommonGA4Params()"
    @confirm="confirmApproval"
    @requestCreated="onRequestCreated"
  />
  <!-- Buradan ayarlanıyor değerler -->
  <ConditionsModal ref="conditionsModalRef" :storeScore="3.5" :paymentCount="2" />
</template>

<script>
import { ref, computed } from 'vue';
import { sendEarlySettlementCheckboxEvent } from '@/utils/ga4/helper';
import { AppButton, TooltipPlugin, Icons } from '@n11/so-ui-components';
import ContractModal from './modals/ContractModal.vue';
import ApprovalModal from './modals/ApprovalModal.vue';
import ConditionsModal from './modals/ConditionsModal.vue';
import { sendRequestEarlyPaymentEvent } from '@/utils/ga4/helper';

export default {
  name: "EarlySettlementRightContent",
  components: {
    AppButton,
    TooltipPlugin,
    Icons,
    ContractModal,
    ApprovalModal,
    ConditionsModal,
  },
  props: {
    isEmpty: {
      type: Boolean,
      default: true,
    },
    calculation: {
      type: Object,
      default: () => null,
    },
    calculationLoading: {
      type: Boolean,
      default: false,
    },
    availability: {
      type: Object,
      default: () => ({}),
    },
    availabilityLoading: {
      type: Boolean,
      default: false,
    },
    summary: {
      type: Object,
      default: () => ({}),
    },
    hasValidationError: {
      type: Boolean,
      default: false,
    },
    isRequestCreated: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['requestCreated'],
  setup(props, { emit }) {
    const isChecked = ref(false);
    const pagePath = typeof window !== "undefined" ? window.location.pathname : "";
    const buttonName = "Erken Ödeme Sözleşmesi'ni okudum ve kabul ediyorum.";

    const isCreationAllowed = computed(() => {
      return !(props.availability?.creationAllowed ?? true);
    });

    const isCurrentBalanceZero = computed(() => {
      if (!props.summary || !props.summary.currentBalance) return false;
      const balance = typeof props.summary.currentBalance === 'string' 
        ? parseFloat(props.summary.currentBalance.replace(/[₺,]/g, '').replace(',', '.')) 
        : parseFloat(props.summary.currentBalance);
      return balance === 0 || isNaN(balance);
    });

    const warningTitle = computed(() => {
      return 'Şu anda erken ödeme talebi oluşturamazsınız';
    });

    const warningMessage = computed(() => {
      return 'Onaylanma sürecinde başka bir erken ödeme <br>talebiniz bulunduğu için şu anda yeni bir talep alamıyoruz.';
    });

    const parseAmount = (value) => {
      if (value === null || value === undefined) return 0;
      if (typeof value === 'number') return value;

      let strValue = String(value);
      strValue = strValue.replace(/₺/g, '').trim();
      strValue = strValue.replace(/\./g, '').replace(',', '.');

      return parseFloat(strValue) || 0;
    };

    const formatToTurkish = (value) => {
      if (value === null || value === undefined) return '';
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      if (isNaN(numValue)) return '';

      const parts = numValue.toString().split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1] || '';

      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

      if (decimalPart) {
        return `₺${formattedInteger},${decimalPart}`;
      }

      return `₺${formattedInteger}`;
    };

    const isMaxRequestableAmountZero = computed(() => {
      if (!props.summary) return false;

      const maxAmount = parseAmount(props.summary.maxRequestableAmount);
      return maxAmount === 0;
    });

    const isBelowMinRequestAmount = computed(() => {
      if (!props.summary) return false;

      const maxAmount = parseAmount(props.summary.maxRequestableAmount);
      const minAmount = parseAmount(props.summary.minRequestAmount);

      if (!minAmount) return false;

      return maxAmount > 0 && maxAmount < minAmount;
    });

    const formattedMinRequestAmount = computed(() => {
      if (!props.summary) return '';

      const minAmount = parseAmount(props.summary.minRequestAmount);
      if (!minAmount) return '';

      const formatted = formatToTurkish(minAmount);
      return formatted.replace('₺', '');
    });

    const handleCheckboxChange = (event) => {
      const eventParams = {
        page_path: pagePath,
        button_name: buttonName
      };
      sendEarlySettlementCheckboxEvent(event.target.checked, eventParams);
    };
    const approvalModalRef = ref(null);
    const contractModalRef = ref(null);
    const conditionsModalRef = ref(null);
    
    const handleClick = () => {
      isChecked.value = !isChecked.value;
    };
    
    const getCommonGA4Params = () => {
      return {
        page_path: typeof window !== "undefined" ? window.location.pathname : "",
        total_amount_requested: props.calculation?.calculatedRequestAmount ?? "",
        days_paid_early: props.calculation?.earlyPaidDayCount ?? "",
        n11_daily_commission_rate: props.calculation?.dailyServiceCommissionRate ?? "",
        service_rate: props.calculation?.appliedServiceRate ?? "",
        service_fee: props.calculation?.serviceFee ?? "",
        tax: props.calculation?.vatAmount ?? "",
        value: props.calculation?.netPayableAmount ?? "",
      };
    };
    
    const openApprovalModal = () => {
      sendRequestEarlyPaymentEvent({
        ...getCommonGA4Params(),
        button_name: "Erken Ödeme Talep Et",
      });
      
      if (approvalModalRef.value) {
        approvalModalRef.value.openModal();
      }
    };
    
    const confirmApproval = (row) => {
      console.log('Erken ödeme talebi onaylandı', row);
    };
    
    const openContractModal = () => {
      if (contractModalRef.value) {
        contractModalRef.value.openModal();
      }
    };
    
    const acceptContract = () => {
      isChecked.value = true;
    };
    
    const openConditionsModal = () => {
      if (conditionsModalRef.value) {
        conditionsModalRef.value.openModal();
      }
    };

    const onRequestCreated = (row) => {
      emit('requestCreated', row);
    };

    return {
      isChecked,
      handleCheckboxChange,
      contractModalRef,
      approvalModalRef,
      conditionsModalRef,
      handleClick,
      openApprovalModal,
      confirmApproval,
      openContractModal,
      acceptContract,
      openConditionsModal,
      onRequestCreated,
      getCommonGA4Params,
      isCreationAllowed,
      isCurrentBalanceZero,
      warningTitle,
      warningMessage,
      isBelowMinRequestAmount,
      formattedMinRequestAmount,
      isMaxRequestableAmountZero,
    };
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.early-settlement-right-content {
  flex: 1;
  padding: 24px;
  background: $colorN00;
  border-radius: 8px;
  display: flex;
  max-width: 648px;
  flex-direction: column;
  gap: 16px;
  height: 490px;
  align-self: stretch;
  justify-content: flex-start;

  &.has-error {
    height: 510px;
  }
  h2 {
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
    color: $colorN80;
  }
  &-empty {
    width: 100%;
    align-items: center;
    justify-content: center;
    gap: 12px 16px;
    display: flex;
    flex-direction: column;
    align-self: stretch;
    .case {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px 16px;
    }
    
    .empty-area, .warning-area {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px 16px;
      flex: 0 0 auto;
      justify-content: center;
      cursor: pointer;
      margin: auto 0;
      p, a {
        text-align: center;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: -0.05px;
        color: $colorN80; 
        b{
          font-weight: 600;
        }
      }
      a {
        color: $colorM50;
        font-weight: 600;
      }
      ul {
        list-style: disc;
        list-style-position: inside;
        line-height: 24px;
        text-align: center;
        li b {
          font-weight: 700;
        }
      }
    }
  }
  
  &-calculation-result {
      display: flex;
      flex-direction: column;
      gap: 16px;
      .loading {
        display: flex;
        width: 100%;
        min-height: 300px;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
      }
      .agreement {
        display: flex;
        align-items: center;
        color: $colorN70;
        font-size: 12px;
        font-weight: 400;
        line-height: 20px;
        gap: 10px;
        a {
          color: $colorN70;
          text-decoration: underline;
          text-decoration-thickness: 4%;
          text-underline-offset: 20%;
          text-underline-position: from-font;
        }
      }
      .balance-area {
        .result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          border-bottom: 0.5px solid $colorN40;
          padding: 8px 8px 7px 8px;

          &:nth-last-child(2) {
            border-bottom: none;
            padding-bottom: 8px;
          }
          
          &-label {
            display: flex;
            align-items: center;
            gap: 6px;
            .info-icon {
              cursor: pointer;
            }
          }
          .result-item-label-right {
            color: $colorN70 !important;
            font-size: 13px !important;
            font-weight: 400 !important;
            line-height: 18px !important;
            letter-spacing: -0.05px !important;
          }
            
          &.total-amount {
            background: $colorM05;
            border-bottom: none;
            border-top: none;
            border-radius: 8px;
            height: 46px;
            span:last-child {
              color: $colorM50;
              font-size: 24px;
            }
          }
          span {
            font-size: 16px;
            font-weight: 400;
            line-height: 20px;
            letter-spacing: -0.02px;
            color: $colorN80;
            
            &:last-child {
              font-weight: 700;
              font-size: 20px;
              line-height: 26px
            }
          }
        }
      }
  }


  input[type='checkbox'] {
    cursor: pointer;
    height: 20px;
    width: 20px;
    appearance: none;
    border: 2px solid $colorN50;
    border-radius: 4px;
    background: $colorN00;
    position: relative;
    margin: 0;
  }

  input[type='checkbox']:checked {
    background-color: $colorN80;
    border-color: $colorN80;
  }

  input[type='checkbox']:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 8px;
    background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='25' height='20' viewBox='0 0 25 20'%3E%3Cpath fill='%23FFF' fill-rule='nonzero' d='M3.161 9.552a1.76 1.76 0 0 0-2.619 0c-.723.778-.723 2.04 0 2.818l6.548 7.046a1.76 1.76 0 0 0 2.624-.006l14.75-16.014c.72-.782.714-2.044-.012-2.819a1.76 1.76 0 0 0-2.62.013L8.394 15.182l-5.232-5.63z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
    transform: translate(-50%, -50%);
  }
  .modal-body {
    width: 580px;
        display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
  .approval-modal-content {
    padding: 20px 0;
    z-index: 9999;
    
    .approval-modal-content-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      .approval-modal-content-item-label {
        display: flex;
        align-items: center;
        gap: 6px;
        
        .approval-modal-content-item-label-right {
          color: $colorN70;
          font-size: 13px;
          font-weight: 400;
          line-height: 18px;
          letter-spacing: -0.05px;
        }
      }
    }
    
    .approval-modal-content-info {
      margin-top: 16px;
      padding: 12px;
      background-color: $colorM05;
      border-radius: 8px;
      height: 68px;
      
      span {
        font-size: 14px;
        color: $colorM20;
        line-height: 20px;
      }
    }
    
    p {
      font-size: 16px;
      color: $colorN80;
      text-align: center;
    }
  }
  
  .contractModal {
    .icon-wrapper {
      display: none !important;
    }
  }
  
  .conditionsModal {
    .icon-wrapper {
      display: none !important;
    }
  }
  
  .contract-modal-content {
    max-height: 500px;
    overflow-y: auto;
    padding: 20px;
    
    .contract-text {
      h3 {
        font-size: 18px;
        font-weight: 600;
        color: $colorN80;
        margin: 24px 0 12px 0;
        
        &:first-child {
          margin-top: 0;
        }
      }
      
      p {
        font-size: 14px;
        color: $colorN70;
        line-height: 20px;
        margin-bottom: 12px;
      }
      
      ul {
        margin: 12px 0;
        padding-left: 20px;
        
        li {
          font-size: 14px;
          color: $colorN70;
          line-height: 20px;
          margin-bottom: 8px;
          
          strong {
            color: $colorN80;
          }
        }
      }
    }
  }
}

.empty-state {
  justify-content: center;
}
</style>
